import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Descriptions, Divider, Drawer, Form, Input, message, Select, Space, Spin, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import {createApi, getApi, getApiPreview, METHOD_TYPES, updateApi} from '../api/apiApi';
import PermissionGate from './common/PermissionGate';
import PermissionButton from './common/PermissionButton';
import {CloseOutlined, CopyOutlined, EditOutlined, PlusOutlined, SaveOutlined} from '@ant-design/icons';

const { Text } = Typography;

function safeJsonPretty(value) {
  if (value == null || value === '') return '-';
  try {
    const obj = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    try { return typeof value === 'string' ? value : JSON.stringify(value, null, 2); } catch { return String(value); }
  }
}

function fmtDate(val) {
  if (!val) return '-';
  const d = dayjs(val);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(val);
}

const InfoItem = ({label, children}) => (
  <Descriptions.Item label={label} span={3}>
    {children}
  </Descriptions.Item>
);

async function copyToClipboard(text, label='내용') {
  try {
    await navigator.clipboard.writeText(text || '');
    message.success(`${label} 복사됨`);
  } catch (e) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text || '';
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success(`${label} 복사됨`);
    } catch (_) {
      message.error('복사에 실패했습니다.');
    }
  }
}

const APIDetailDrawer = ({ open, apiId, record, onClose, onRegistered, initialEditMode=false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  // 미등록 API 프리뷰 상태
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // 수정 모드 상태 및 폼
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const id = apiId || record?.apiId;

  useEffect(() => { setEditMode(Boolean(initialEditMode)); }, [initialEditMode, open]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      if (!open) return;
      if (!id) { setDetail(record || null); return; }
      setLoading(true); setError(null);
      try {
        const res = await getApi(id);
        if (mounted) setDetail(res);
      } catch (e) { if (mounted) setError(e); }
      finally { if (mounted) setLoading(false); }
    };
    fetch();
    return () => { mounted = false; };
  }, [open, id, record]);

  // 목록 record의 registerType을 detail 조회 후에도 보존하기 위해 병합 순서를 record -> detail 로 유지
  const merged = useMemo(() => ({ ...(record || {}), ...(detail || {}) }), [detail, record]);

  // 미등록인 경우 Drawer 표시 시 자동 프리뷰 조회
  useEffect(() => {
    let mounted = true;
    const autoPreview = async () => {
      if (!open) return;
      if (merged?.apiId) { setPreview(null); setPreviewError(null); return; }
      if (!merged?.methodType || !merged?.apiUrl) return;
      setPreviewLoading(true); setPreviewError(null);
      try {
        const res = await getApiPreview(merged.methodType, merged.apiUrl);
        if (mounted) setPreview(res);
      } catch (e) { if (mounted) setPreviewError(e); }
      finally { if (mounted) setPreviewLoading(false); }
    };
    autoPreview();
    return () => { mounted = false; };
  }, [open, merged.apiId, merged.methodType, merged.apiUrl]);

  // 수정 모드 진입 시 폼 값 세팅
  useEffect(() => {
    if (editMode && open) {
      form.setFieldsValue({
        methodType: merged.methodType,
        apiNm: merged.apiNm,
        apiUrl: merged.apiUrl,
        apiDsctn: merged.apiDsctn,
        header: typeof merged.header === 'string' ? merged.header : (merged.header ? JSON.stringify(merged.header, null, 2) : undefined),
        body: typeof merged.body === 'string' ? merged.body : (merged.body ? JSON.stringify(merged.body, null, 2) : undefined),
        response: typeof merged.response === 'string' ? merged.response : (merged.response ? JSON.stringify(merged.response, null, 2) : undefined),
      });
    } else if (!editMode) { form.resetFields(); }
  }, [editMode, open, merged, form]);

  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (id) {
        await updateApi(id, values);
        message.success('수정되었습니다.');
        setEditMode(false);
        const res = await getApi(id);
        setDetail(res);
        onRegistered?.();
      } else {
        // 생성 모드: header/body/response가 비어 있고 method/url이 있으면 preview 병합 시도
        let payload = { ...values };
        const hasMethodUrl = values?.methodType && values?.apiUrl;
        const hasExamples = values?.header || values?.body || values?.response;
        if (hasMethodUrl && !hasExamples) {
          try {
            const pv = await getApiPreview(values.methodType, values.apiUrl);
            payload = { ...payload, header: pv?.header, body: pv?.body, response: pv?.response };
          } catch (_) { /* ignore preview fail */ }
        }
        await createApi(payload);
        message.success('생성되었습니다.');
        onRegistered?.();
        onClose?.();
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(id ? '수정에 실패했습니다.' : '생성에 실패했습니다.');
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    if (id) { setEditMode(false); }
    else { try { form.resetFields(); } catch(_) {} onClose?.(); }
  };

  const handleRegister = async () => {
    if (merged?.apiId) return;
    if (!merged?.methodType || !merged?.apiUrl) { message.warning('Method와 URL이 필요합니다.'); return; }
    try {
      setSaving(true);
      // 프리뷰가 없다면 한번 더 시도
      let pv = preview;
      if (!pv) { try { pv = await getApiPreview(merged.methodType, merged.apiUrl); } catch (e) { pv = null; } }
      const payload = {
        methodType: merged.methodType,
        apiNm: merged.apiNm || `${merged.methodType} ${merged.apiUrl}`,
        apiUrl: merged.apiUrl,
        apiDsctn: merged.apiDsctn,
        header: pv?.header,
        body: pv?.body,
        response: pv?.response,
      };
      await createApi(payload);
      message.success('등록되었습니다.');
      onRegistered?.();
      onClose?.();
    } catch (e) { message.error('등록에 실패했습니다.'); }
    finally { setSaving(false); }
  };

  const title = useMemo(() => {
    const m = merged.methodType || '-';
    const n = merged.apiNm || '-';
    return `${m} ${n}`;
  }, [merged]);

  const resolveRegisterLabel = (r) => {
    const t = (r?.registerType || '').trim();
    if (t === '삭제') return '삭제';
    if (t === '변경') return '변경';
    if (t === '미등록') return '미등록';
    if (r?.apiId) return '등록';
    return '미등록';
  };
  const resolveRegisterColor = (label) => {
    switch (label) {
      case '삭제': return 'volcano';
      case '변경': return 'orange';
      case '등록': return 'green';
      case '미등록': return 'red';
      default: return 'default';
    }
  };

  const statusTag = () => {
    const label = resolveRegisterLabel(merged);
    const color = resolveRegisterColor(label);
    return <Tag color={color}>{label}</Tag>;
  };

  const showHeader = merged.apiId ? merged.header : (preview?.header);
  const showBody = merged.apiId ? merged.body : (preview?.body);
  const showResponse = merged.apiId ? merged.response : (preview?.response);

  // 미리 포맷된 문자열 (복사/출력 공용)
  const headerStr = safeJsonPretty(showHeader);
  const bodyStr = safeJsonPretty(showBody);
  const responseStr = safeJsonPretty(showResponse);

  const preStyle = { whiteSpace:'pre-wrap', maxHeight:'60vh', overflow:'auto', fontFamily:'Menlo, Monaco, Consolas, monospace' };

  const headerExtra = (
    <Space size={8} wrap>
      {editMode ? (
        <>
          <PermissionButton type="primary" icon={<SaveOutlined />} loading={saving} require={id ? updateApi : createApi} onClick={handleSaveEdit}>
            저장
          </PermissionButton>
          <PermissionButton icon={<CloseOutlined />} onClick={handleCancel}>
            취소
          </PermissionButton>
        </>
      ) : (
        merged.apiId ? (
          <PermissionButton type="primary" icon={<EditOutlined />} require={updateApi} onClick={() => setEditMode(true)}>
            수정
          </PermissionButton>
        ) : (
          <PermissionButton type="primary" icon={<PlusOutlined />} loading={saving} require={createApi} onClick={handleRegister}>
            등록
          </PermissionButton>
        )
      )}
    </Space>
  );

  return (
    <Drawer open={open} onClose={onClose} width={720} title={<Space>{statusTag()}<span>{title}</span></Space>} extra={headerExtra}>
      <PermissionGate require={id ? getApi : createApi}>
        {({allowed}) => (
          <div style={{ opacity: allowed ? 1 : 0.6 }}>
            {(loading || previewLoading) && <Spin />}
            {error && <Alert type="error" showIcon message={id ? '상세 조회 실패' : '권한 확인'} description={String(error?.message || '오류가 발생했습니다.')} style={{ marginBottom: 12 }} />}
            {/* 삭제 상태 경고 */}
            {resolveRegisterLabel(merged) === '삭제' && (
              <Alert type="warning" showIcon message="Suggester에 존재하지 않는 API입니다(삭제 대상)." style={{ marginBottom: 12 }} />
            )}
            {previewError && !merged.apiId && (
              <Alert type="warning" showIcon message="프리뷰 조회 실패" description={String(previewError?.message || 'Swagger 기반 예시를 불러오지 못했습니다.')} style={{ marginBottom: 12 }} />
            )}

            {!editMode && (
              <>
                <Descriptions column={1} size="small" bordered>
                  <InfoItem label="Method">
                    <Tag color="blue">{merged.methodType || '-'}</Tag>
                  </InfoItem>
                  <InfoItem label="API명">{merged.apiNm || '-'}</InfoItem>
                  <InfoItem label="URL"><Text code>{merged.apiUrl || '-'}</Text></InfoItem>
                  <InfoItem label="설명">{merged.apiDsctn || '-'}</InfoItem>
                  {(merged.tags?.length > 0) && (
                    <InfoItem label="태그">{(merged.tags || []).map((t,i)=>(<Tag key={i}>{t}</Tag>))}</InfoItem>
                  )}
                  {(merged.status) && (
                    <InfoItem label="상태"><Tag>{merged.status}</Tag></InfoItem>
                  )}
                  {(merged.version != null) && (
                    <InfoItem label="버전">{merged.version}</InfoItem>
                  )}
                  <InfoItem label="등록일시">{fmtDate(merged.regDt)}</InfoItem>
                  <InfoItem label="등록자">{merged.rgtr || '-'}</InfoItem>
                  <InfoItem label="수정일시">{fmtDate(merged.mdfcnDt)}</InfoItem>
                  <InfoItem label="수정자">{merged.mdfr || '-'}</InfoItem>
                </Descriptions>

                <Divider orientation="left">Request Header</Divider>
                <div style={{ display:'flex', justifyContent:'flex-end', margin: '4px 0' }}>
                  <PermissionButton size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(headerStr, 'Request Header')}>복사</PermissionButton>
                </div>
                <pre style={preStyle}>{headerStr}</pre>

                <Divider orientation="left">Request Body</Divider>
                <div style={{ display:'flex', justifyContent:'flex-end', margin: '4px 0' }}>
                  <PermissionButton size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(bodyStr, 'Request Body')}>복사</PermissionButton>
                </div>
                <pre style={preStyle}>{bodyStr}</pre>

                <Divider orientation="left">Response</Divider>
                <div style={{ display:'flex', justifyContent:'flex-end', margin: '4px 0' }}>
                  <PermissionButton size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(responseStr, 'Response')}>복사</PermissionButton>
                </div>
                <pre style={preStyle}>{responseStr}</pre>
              </>
            )}

            {editMode && (
              <Form layout="vertical" form={form} style={{ marginTop: 8 }}>
                <Form.Item name="methodType" label="Method" rules={[{ required: true, message: 'Method 선택' }]}>
                  <Select options={METHOD_TYPES.map(m=>({value:m,label:m}))} placeholder="선택" />
                </Form.Item>
                <Form.Item name="apiNm" label="API명" rules={[{ required: true, message: 'API명 입력' }]}>
                  <Input maxLength={150} />
                </Form.Item>
                <Form.Item name="apiUrl" label="URL" rules={[{ required: true, message: 'URL 입력' }]}>
                  <Input maxLength={300} placeholder="/v1.0/sample" />
                </Form.Item>
                <Form.Item name="apiDsctn" label="설명">
                  <Input.TextArea rows={3} maxLength={1000} />
                </Form.Item>
                <Form.Item name="header" label="Header(JSON)">
                  <Input.TextArea rows={4} placeholder='{"Authorization":"Bearer ..."}' />
                </Form.Item>
                <Form.Item name="body" label="Body(JSON)">
                  <Input.TextArea rows={6} placeholder='{"sample":"value"}' />
                </Form.Item>
                <Form.Item name="response" label="Response(JSON)">
                  <Input.TextArea rows={6} placeholder='{"code":200,"data":{...}}' />
                </Form.Item>
              </Form>
            )}
          </div>
        )}
      </PermissionGate>
    </Drawer>
  );
};

export default APIDetailDrawer;
